import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Durée d'inactivité avant déconnexion (15 minutes par défaut)
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes en millisecondes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem('token');
      const userData = authService.getCurrentUser();

      if (token && userData) {
        setUser(userData);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (identifier, password) => {
    try {
      const response = await authService.login(identifier, password);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData, profilePhotoFile) => {
    try {
      const response = await authService.register(userData, profilePhotoFile);

      // Seulement stocker les données si l'utilisateur est connecté (pas de vérification email requise)
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    // Nettoyer le timer d'inactivité
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const updateUser = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  // Fonction pour réinitialiser le timer d'inactivité
  const resetInactivityTimer = useCallback(() => {
    // Nettoyer le timer existant
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Ne pas démarrer le timer si l'utilisateur n'est pas connecté
    if (!user) {
      return;
    }

    // Créer un nouveau timer
    inactivityTimerRef.current = setTimeout(() => {
      // console.log('Déconnexion automatique après inactivité');

      // Déconnecter l'utilisateur
      logout();

      // Rediriger vers la page de login avec un message
      // On utilise sessionStorage pour passer le message car localStorage est nettoyé par logout()
      // sessionStorage.setItem('logoutMessage', 'Vous avez été déconnecté automatiquement après une période d\'inactivité de 5 minutes.');

      // Rediriger vers la page de login
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }, [user, logout]);

  // Fonction pour démarrer le monitoring d'inactivité
  const startInactivityMonitoring = useCallback(() => {
    if (!user) {
      return;
    }

    // Liste des événements qui indiquent une activité
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Fonction de gestion des événements
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Ajouter les listeners pour tous les événements d'activité
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Démarrer le timer initial
    resetInactivityTimer();

    // Fonction de nettoyage
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user, resetInactivityTimer]);

  // Démarrer le monitoring quand l'utilisateur est connecté
  useEffect(() => {
    if (user && !loading) {
      const cleanup = startInactivityMonitoring();
      return cleanup;
    }
  }, [user, loading, startInactivityMonitoring]);

  // Gérer la visibilité de l'onglet (pause/resume le timer)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // L'onglet est caché - pause le timer (optionnel, vous pouvez le laisser continuer)
        // Pour cet implémentation, on continue à compter même quand l'onglet est caché
      } else {
        // L'onglet est visible - réinitialiser le timer
        if (user) {
          resetInactivityTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetInactivityTimer]);

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
